// ============================================================
// SINGLE SOURCE OF TRUTH — edit links, copy, asset paths here.
// ============================================================

export const PROFILE = {
  name: 'Vedant Kayandekar',
  roleLine: ['Software Engineer', 'Full-Stack Developer', 'Coach & Creator'],
  location: 'Computer Engineering · Mumbai, India',
  intro:
    "I'm Vedant — a 2026 computer engineering graduate who enjoys turning ideas into working products: building full-stack applications, experimenting with AI, and running my own ventures. I learn by building, testing, and taking ownership.",
  email: 'vedantassociates2004@gmail.com',
  phone: '+91 9167375587',
}

export const LINKS = {
  github: 'https://github.com/vedant-kayandekar',
  linkedin: 'https://www.linkedin.com/in/vedant-kayandekar/',
  email: 'mailto:vedantassociates2004@gmail.com',
  playongear: 'https://playongear.com',
  anycoaches: 'https://anycoaches.playongear.com',
  yogakickfit: 'https://yogakickfit.playongear.com',
  goldenOrchard: 'https://goldenorchardstays.com/',
  repoFraud: 'https://github.com/vedant-kayandekar/fraud-detection-backend',
  repoP2P:
    'https://github.com/vedant-kayandekar/p2p-and-grp-videocall-app-and-aslo-omegle-feature',
  repoInventory:
    'https://github.com/vedant-kayandekar/Inventory-Management-Python-Django',
}

// Drop your voice recording at frontend/public/audio/story-narration.mp3
export const AUDIO = {
  src: '/audio/story-narration.mp3',
  label: 'My story, in my voice',
}

// 3D assets (local — no remote HDRI / CDN dependencies)
export const MODELS = {
  idle: '/models/animated_model.glb',
  landing: '/models/landing_model.glb',
  hdr: '/environment/potsdamer_platz_1k.hdr',
}

export const NARRATIONS = {
  home: {
    file: '/audio/hero.mp3',
    caption: "Hey, I'm Vedant — I want to show you, not tell you.",
  },
  experience: {
    file: '/audio/experience.mp3',
    caption: 'How PlayOnGear and AnyCoaches taught me ownership.',
  },
  skills: {
    file: '/audio/skills.mp3',
    caption: 'Learning technology by building with it.',
  },
  projects: {
    file: '/audio/projects.mp3',
    caption: 'FinLens, the yoga companion, two national podiums.',
  },
  gallery: {
    file: '/audio/creator.mp3',
    caption: 'The creator side — one million views and counting.',
  },
  story: {
    file: '/audio/story.mp3',
    caption: 'The main story, in my own voice.',
  },
  contact: {
    file: '/audio/contact.mp3',
    caption: "Thanks for taking the time to know my story.",
  },
}

export const SECTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'story', label: 'Story' },
  { id: 'contact', label: 'Contact' },
]

// Nav pill adapts to the visual world of the active section
export const SECTION_THEMES = {
  home: { dark: true, accent: '#ff6a30', activeText: '#0d0d0d' },
  experience: { dark: false, accent: '#16130f', activeText: '#f4ede1' },
  skills: { dark: false, accent: '#16130f', activeText: '#f5f3ef' },
  projects: { dark: false, accent: '#0e0e0e', activeText: '#ffcf3f' },
  gallery: { dark: true, accent: '#ffb52e', activeText: '#160c05' },
  story: { dark: true, accent: '#e8a94b', activeText: '#0a0806' },
  contact: { dark: true, accent: '#ffffff', activeText: '#1c1440' },
}

export const EXPERIENCE_RUNGS = [
  {
    step: 'Rung 01',
    title: 'Software Developer Intern',
    org: 'Innovative Informatics Pvt. Ltd.',
    period: 'May 2025 – Jul 2025',
    color: 'var(--exp-coral)',
    align: 'left',
    points: [
      'Worked on an enterprise expense-management ERP — C#, .NET Core, SQL Server, React.',
      'Designed the expense-voucher workflow and shipped full-CRUD REST APIs end-to-end.',
      'Optimised stored procedures, indexing and triggers on high-traffic production tables.',
    ],
  },
  {
    step: 'Rung 02',
    title: 'Founder-Led Ventures',
    org: 'PlayOnGear & AnyCoaches',
    period: '2025 – Present',
    color: 'var(--exp-teal)',
    align: 'right',
    points: [
      'PlayOnGear — sports e-commerce venture, owned end-to-end: product, authentication, payments, hosting, deployment.',
      'AnyCoaches — coach–student marketplace, full-stack build and live operations.',
      'Sole architect and engineer across two live products on two different stacks.',
    ],
    links: [
      { label: 'playongear.com', href: LINKS.playongear },
      { label: 'anycoaches.playongear.com', href: LINKS.anycoaches },
    ],
  },
  {
    step: 'Rung 03',
    title: '2 National Hackathon Podiums',
    org: 'TechSagar 2026 · Hacktech Fusion 2025',
    period: '2025 – 2026',
    color: 'var(--exp-purple)',
    align: 'left',
    points: [
      'FinLens — real-time fraud detection, 2nd place at TechSagar 2026.',
      'AI 3D Yoga Companion — 2nd place at Hacktech Fusion 2025.',
      'Deployed full stacks solo under live-demo pressure — both projects work on stage.',
    ],
  },
]

export const SKILLS = [
  { name: 'Python', color: '#3776ab' },
  { name: 'SQL', color: '#0f7f8c' },
  { name: 'Shopify', color: '#95bf47' },
  { name: 'JavaScript', color: '#e8c547', dark: true },
  { name: 'React', color: '#20232a' },
  { name: 'Node.js', color: '#3c873a' },
  { name: '.NET Core', color: '#512bd4' },
  { name: 'Django', color: '#0c4b33' },
  { name: 'PostgreSQL', color: '#336791' },
  { name: 'MySQL', color: '#4479a1' },
  { name: 'SQL Server', color: '#a4373a' },
]

export const PROJECTS = [
  {
    tag: 'HACKATHON · 2ND PLACE',
    title: 'AI 3D Yoga Companion',
    desc: 'Real-time pose detection and correction with a 3D avatar and voice interaction. RAG via pgvector on PostgreSQL, Gemini for reasoning, computer-vision pose pipeline.',
    stack: ['AI/RAG', '3D Avatar', 'Gemini', 'PostgreSQL', 'pgvector', 'Computer Vision'],
    link: LINKS.yogakickfit,
    linkLabel: 'yogakickfit.playongear.com',
    image: '/projects/yoga_companion_preview.jpg',
    big: true,
  },
  {
    tag: 'VENTURE · FOUNDER',
    title: 'PlayOnGear & AnyCoaches',
    desc: 'Founder-led products built and managed end-to-end — sports e-commerce and a coach–student marketplace. Product, auth, payments, database, hosting, deployment.',
    stack: ['React', 'Full-Stack', 'Payments', 'Deployment'],
    link: LINKS.playongear,
    linkLabel: 'playongear.com',
    secondLink: { label: 'anycoaches.playongear.com', href: LINKS.anycoaches },
    images: ['/projects/playongear_preview.jpg', '/projects/anycoaches_preview.jpg'],
    big: true,
  },
  {
    tag: 'HACKATHON · 2ND PLACE',
    title: 'FinLens — Fake Transaction Fraud Detection',
    desc: 'Fraud-detection platform — trained and benchmarked four ML models in parallel, streamed the strongest result first so the demo felt instant.',
    stack: ['Python', 'FastAPI', 'React', 'Supabase'],
    github: LINKS.repoFraud,
  },
  {
    tag: 'CLIENT WORK · LIVE',
    title: 'Golden Orchard Stays',
    desc: 'Luxury villa booking engine for a real client — live site, real bookings flow.',
    stack: ['Villa Booking', 'Client Project'],
    link: LINKS.goldenOrchard,
    linkLabel: 'goldenorchardstays.com',
    image: '/projects/golden_orchard_stays_preview.jpg',
  },
  {
    tag: 'PROJECT',
    title: 'P2P WebRTC Video Call',
    desc: 'Real-time 1:1 and group video conferencing on raw WebRTC, plus an Omegle-style random-match feature.',
    stack: ['Python', 'Django', 'WebRTC', 'WebAssembly'],
    github: LINKS.repoP2P,
  },
  {
    tag: 'PROJECT',
    title: 'Inventory & CRM System',
    desc: 'Stock tracking, order management and CRM for small and medium businesses.',
    stack: ['Python', 'Django', 'SQLite'],
    github: LINKS.repoInventory,
  },
]

export const GALLERY_STAGES = [
  {
    id: 'intern',
    tag: 'STAGE 01',
    title: 'Software Developer Intern',
    org: 'Innovative Informatics Pvt. Ltd.',
    caption: 'Shipping a live enterprise expense-management ERP — React, .NET Core, SQL.',
    img1: '/gallery/intern-1.jpg',
    img2: '/gallery/intern-2.jpg',
    imageSide: 'left',
    accent: 'var(--gallery-gold)',
  },
  {
    id: 'finlens',
    tag: 'STAGE 02',
    title: 'FinLens — 2nd Place',
    org: 'TechSagar 2026, National Hackathon',
    caption: 'Led a 3-person team benchmarking 4 ML models for real-time fraud detection.',
    img1: '/gallery/hackathon-2-a.jpg',
    img2: '/gallery/hackathon-2-b.jpg',
    imageSide: 'right',
    accent: 'var(--gallery-fire)',
  },
  {
    id: 'yoga',
    tag: 'STAGE 03',
    title: 'AI Yoga Companion — 2nd Place',
    org: 'Hacktech Fusion 2025',
    caption: 'RAG + pgvector + real-time pose detection, with a 3D companion built in Blender.',
    img1: '/gallery/hackathon-1-a.jpg',
    img2: '/gallery/hackathon-1-b.jpg',
    imageSide: 'left',
    accent: 'var(--gallery-gold)',
  },
]

export const STORY = {
  kicker: "a one-minute listen, then it's your call",
  heading: ['Not a pitch.', 'Just me.'],
  paragraphs: [
    'I wanted to introduce myself differently this time — not the same static thing I send everywhere.',
    "I'm Vedant. Who am I? A hard worker and a fast learner. It might sound like a line out of a Jake Gyllenhaal movie — Nightcrawler — but it's genuinely who I am.",
    "If there's something new put in front of me — some technology I've never touched — I will work until I master it. I will not give up on it. It doesn't matter if I can't sleep for it, it doesn't matter how many hours it takes. Until the work is finished, I don't stop. That's my way of doing things.",
    "I'm passionate about technology, and whatever I get interested in, I go deep into it — not just to say I did it, but to actually finish it.",
  ],
  closing: ['Give me the opportunity.', 'I will not let you down.'],
}
